import React from "react";
import { useTranslation } from "react-i18next";
import useFeatureFlag from "../../../hooks/useFeatureFlag";
import { buildingStore } from "store/Building/BuildingStore";
import { UPCOMING_OPERATION_LIST } from "features/Building/consts.d";
import {
  IconApps,
  IconBolt,
  IconBusinessplan,
  IconChartArrowsVertical,
  IconChartBar,
  IconDatabase,
  IconDatabaseSmile,
  IconDownload,
  IconHexagon,
  IconLayoutColumns,
  IconLayoutDashboard,
  IconCurrencyEuro,
  IconLeaf,
  IconList,
  IconRefresh,
} from "@tabler/icons-react";
import { IconBricks } from "utils";
import { MenuItemProps } from "features/Building/BuildingMenu/MenuItem";
import { useFeatureAccess } from "../../../hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";
import { ROUTES_ENUM } from "routes/consts";
import DGNBIcon from "assets/images/dgnb_logo.svg";
import QNGIcon from "assets/images/qng_logo.svg";
import LevelsIcon from "assets/images/levels_logo.svg";
import { useComplianceInfo } from "../../../hooks/useComplianceInfo";
import { buildingSettingsStore } from "features/Building/BuildingSettings/BuildingSettingsStore";
import { useNavigate } from "react-router";
import { conformityStore } from "../../../store/ConformityStore.ts";

const useBuildingMenuOptions = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const { isFeatureVisible } = useFeatureFlag();
  const isCertificationDashboardEnabled = isFeatureVisible(
    "certification_dashboards"
  );
  const isLevelsDashboardEnabled = isFeatureVisible("levels_dashboard");
  const { getCurrentCertification } = useComplianceInfo();
  const navigate = useNavigate();

  const getActiveCertName = () => {
    const activeCert = getCurrentCertification(
      buildingStore.currentBuilding?.certification_id
    );
    return isEng ? activeCert?.name_en : activeCert?.name_de;
  };

  const getRequestedCert = (type: string) =>
    conformityStore.certificationOptions.find((item) =>
      item.name.includes(type)
    );

  const onConfirmChangeCertificate = (type: string, route: string) => {
    const newCert = getRequestedCert(type);
    if (!newCert) return;
    buildingSettingsStore.fetchUpdateBuilding({
      certification_id: newCert.id,
      conformity_id: newCert.conformities[0].id,
    });
    navigate(route);
  };

  const onCancel = () => {
    buildingStore.setOpenMenuCheckerModal(undefined);
  };

  const hasCertificationDashboard = () => {
    const certification_export_settings =
      buildingStore.currentBuilding?.certification_export_settings;
    if (!certification_export_settings) return;

    return (
      Object.values(certification_export_settings)
        .filter(Boolean)
        ?.some((item) => item?.is_active) && isCertificationDashboardEnabled
    );
  };

  const { hasFeature } = useFeatureAccess();

  const isNewBuilding = () => {
    return (
      buildingStore.currentBuilding?.upcoming_operation ===
      UPCOMING_OPERATION_LIST[0].id
    );
  };

  const energyDashboardEnabled =
    buildingSettingsStore.hasAnyActiveCertifications() &&
    !buildingStore.buildingHasKeineCertification();

  function analyzeSubMenu() {
    const isLCCDashboardEnabled = isFeatureVisible("lcc_dashboard");

    const dashboardItems = [
      {
        name: t("building.environmentalImpact"),
        path: "analytics",
        icon: <IconLeaf />,
        subtitle: t("building.environmentalImpactSubtitle"),
      },
      {
        name: t("building.materialValue"),
        path: "material-value",
        icon: <IconBusinessplan />,
        subtitle: t("building.materialValueSubtitle"),
      },
      {
        name: t("dashboards.circularity"),
        path: "circularity",
        icon: <IconRefresh />,
        subtitle: t("building.circularitySubtitle"),
      },
      {
        name: t("building.energy"),
        path: "energy",
        icon: <IconBolt />,
        subtitle: t("building.energySubtitle"),
        tooltip: energyDashboardEnabled
          ? ""
          : t("building.energyDashboardTooltip"),
        disable: !energyDashboardEnabled,
      },
      {
        name: t("building.dataQuality"),
        path: "data-quality",
        icon: <IconDatabaseSmile />,
        subtitle: t("building.dataQualitySubtitle"),
      },
      {
        hidden: !isLCCDashboardEnabled,
        name: t("lcc.menuTitle"),
        path: "lcc",
        icon: <IconCurrencyEuro />,
        subtitle: t("lcc.lifecycleCostingSubtitle"),
      },
    ];

    const toolsItems = [
      {
        disable: !hasFeature(LicenseFeatureEnum.VariantComparison),
        name: t("variants.title"),
        path: "variants",
        icon: <IconLayoutColumns />,
        subtitle: t("variants.subtitle"),
        tooltip: t("buildingMaterialResources.exportsNoLicenseTooltip"),
      },
      {
        name: "CO₂ Hotspot Analyzer",
        path: "hotspot-analyzer",
        icon: <IconChartArrowsVertical />,
        subtitle: t("building.hotspotAnalyzerSubtitle"),
      },
    ];

    const certificationItems = [
      {
        name: "DGNB 2018",
        path: getActiveCertName()?.includes("2018")
          ? ROUTES_ENUM.DGNB18_DASHBOARD
          : undefined,
        icon: <img src={DGNBIcon} alt="DGNB Logo" className="min-w-[36px]" />,
        subtitle: t("building.dgnb18MenuSubtitle"),
        openConfirmModal: () => {
          buildingStore.setOpenMenuCheckerModal({
            menuID: "2018",
            open: true,
            close: onCancel,
            confirm: () =>
              onConfirmChangeCertificate("2018", ROUTES_ENUM.DGNB18_DASHBOARD),
            confirmButtonTitle: t("building.recalculateMappings"),
            description: t("building.recalculateProductMappingsDesc", {
              active_certification: getActiveCertName(),
              requested_certification: isEng
                ? getRequestedCert("2018")?.name_en
                : getRequestedCert("2018")?.name_de,
            }),
            title: t("building.recalculateProductMappingsTitle"),
          });
        },
      },
      {
        name: "DGNB 2023",
        path: getActiveCertName()?.includes("2023")
          ? ROUTES_ENUM.DGNB23_DASHBOARD
          : undefined,
        icon: <img src={DGNBIcon} alt="DGNB Logo" className="min-w-[36px]" />,
        subtitle: t("building.dgnb23MenuSubtitle"),
        openConfirmModal: () => {
          buildingStore.setOpenMenuCheckerModal({
            menuID: "2023",
            open: true,
            close: onCancel,
            confirm: () =>
              onConfirmChangeCertificate("2023", ROUTES_ENUM.DGNB23_DASHBOARD),
            confirmButtonTitle: t("building.recalculateMappings"),
            description: t("building.recalculateProductMappingsDesc", {
              active_certification: getActiveCertName(),
              requested_certification: isEng
                ? getRequestedCert("2023")?.name_en
                : getRequestedCert("2023")?.name_de,
            }),
            title: t("building.recalculateProductMappingsTitle"),
          });
        },
      },
      {
        name: t("building.qngDashboardMenu"),
        path: getActiveCertName()?.includes("QNG")
          ? ROUTES_ENUM.QNG_DASHBOARD
          : undefined,
        icon: <img src={QNGIcon} alt="QNG Logo" className="min-w-[36px]" />,
        subtitle: t("building.qngDashboardSubtitle"),
        openConfirmModal: () => {
          buildingStore.setOpenMenuCheckerModal({
            menuID: "QNG",
            open: true,
            close: onCancel,
            confirm: () =>
              onConfirmChangeCertificate("QNG", ROUTES_ENUM.QNG_DASHBOARD),
            confirmButtonTitle: t("building.recalculateMappings"),
            description: t("building.recalculateProductMappingsDesc", {
              active_certification: getActiveCertName(),
              requested_certification: isEng
                ? getRequestedCert("QNG")?.name_en
                : getRequestedCert("QNG")?.name_de,
            }),
            title: t("building.recalculateProductMappingsTitle"),
          });
        },
      },
      {
        hidden: !isLevelsDashboardEnabled,
        name: t("levels.menuTitle"),
        path: getActiveCertName()?.toLowerCase().includes("level")
          ? ROUTES_ENUM.LEVELS_DASHBOARD
          : undefined,
        icon: (
          <img src={LevelsIcon} alt="Level(s) Logo" className="min-w-[36px]" />
        ),
        subtitle: t("levels.menuSubtitle"),
        openConfirmModal: () => {
          buildingStore.setOpenMenuCheckerModal({
            menuID: "Level",
            open: true,
            close: onCancel,
            confirm: () =>
              onConfirmChangeCertificate("Level", ROUTES_ENUM.LEVELS_DASHBOARD),
            confirmButtonTitle: t("building.recalculateMappings"),
            description: t("building.recalculateProductMappingsDesc", {
              active_certification: getActiveCertName(),
              requested_certification: isEng
                ? getRequestedCert("Level")?.name_en
                : getRequestedCert("Level")?.name_de,
            }),
            title: t("building.recalculateProductMappingsTitle"),
          });
        },
      },
    ];

    return [
      {
        title: "DASHBOARDS",
        items: dashboardItems.filter((item) => !item.hidden),
      },
      {
        hidden: !hasCertificationDashboard(),
        title: t("building.certificationAndSeal"),
        items: certificationItems.filter((item) => !item.hidden),
      },
      { title: "TOOLS", items: toolsItems },
    ];
  }

  const showNewInventory =
    hasFeature(LicenseFeatureEnum.Inventory) &&
    isFeatureVisible("new_inventory");

  const options: MenuItemProps[] = [
    {
      name: t("buildingPassport.passport"),
      path: ROUTES_ENUM.PASSPORT,
      icon: <IconLayoutDashboard />,
    },
    {
      hidden: !hasFeature(LicenseFeatureEnum.Inventory) || showNewInventory,
      name: t("buildingInventory.inventory"),
      path: ROUTES_ENUM.INVENTORY,
      icon: <IconList />,
    },
    {
      hidden: !showNewInventory,
      name: t("buildingInventory.inventory"),
      path: ROUTES_ENUM.NEW_INVENTORY,
      icon: <IconList />,
    },
    {
      hidden: !hasFeature(LicenseFeatureEnum.AnalyticsDashboards),
      name: t("dashboards.analytics"),
      icon: <IconChartBar />,
      subMenu: analyzeSubMenu().filter((rec) => !rec.hidden),
    },
    {
      name: t("exports.title"),
      path: ROUTES_ENUM.EXPORTS,
      icon: <IconDownload />,
    },
    {
      hidden: !hasFeature("integrations_menu"),
      name: t("building.integration"),
      icon: <IconApps />,
      subMenu: [
        {
          title: "",
          items: [
            {
              name: "eeule",
              path: ROUTES_ENUM.EEULE,
              icon: <IconLayoutDashboard />,
              subtitle: t("building.eEuleDescription"),
            },
          ],
        },
      ],
    },
    {
      hidden: !hasFeature(LicenseFeatureEnum.Audits) || isNewBuilding(),
      name: t("commons.audit_material_sales"),
      path: ROUTES_ENUM.AUDIT_MATERIAL_SALES,
      icon: IconBricks,
    },
    {
      hidden: !hasFeature("ai_tab"),
      name: t("building.aiAssistant"),
      path: ROUTES_ENUM.AI,
    },
  ];

  const isSourceFileDisabled =
    !hasFeature(LicenseFeatureEnum.BuildingDataUploadIfcCsv) &&
    buildingStore.sourceFiles.items.length === 0;

  const rightOptions: MenuItemProps[] = [
    {
      name: t("buildingPassport.sourceData"),
      icon: <IconDatabase />,
      action: "hover",
      items: [
        {
          disable: isSourceFileDisabled,
          name: t("buildingPassport.objectFiles"),
          path: "source-files",
          icon: <IconHexagon />,
          tooltip: isSourceFileDisabled
            ? t("buildingPassport.sourceFilesDisabledTooltip")
            : "",
          subtitle: t("buildingPassport.sourceFilesSubtitle"),
        },
        {
          disable: isSourceFileDisabled,
          name: t("operationalEnergy.title"),
          path: "operational-energy",
          icon: <IconBolt />,
          subtitle: t("buildingPassport.operationalEnergySubtitle"),
        },
      ],
    },
  ];

  const visibleMenuOptions = options.filter((option) => !option.hidden);
  const visiblbleRightOptions = rightOptions.filter((option) => !option.hidden);

  return {
    options: visibleMenuOptions,
    rightOptions: visiblbleRightOptions,
  };
};

export default useBuildingMenuOptions;
